import { gql, useQuery } from '@apollo/client';

import { SubscriptionStatus } from '../../__generated__/globalTypes';
import { useMembershipAccess, useViewerCanManageBilling } from '../queries/useMembershipAccess';

import type {
  AssistantLapsedMembershipQuery,
  AssistantLapsedMembershipQueryVariables,
} from './__generated__/useAssistantMembershipPause.gql';

const ASSISTANT_LAPSED_MEMBERSHIP = gql`
  query AssistantLapsedMembership {
    currentSubscription {
      status
      currentPeriodEnd
    }
  }
`;

/**
 * Why the assistant is paused, in the terms its copy needs.
 *
 * `unknown` covers a subscription read still in flight, one that failed, and
 * every loved one's pause — a `DEPENDENT` is denied `subscriptions`, so the
 * read is never issued for one, and the household's billing is the
 * caregiver's to act on.
 */
export type AssistantMembershipPause =
  | { kind: 'lapsed'; endedAt: Date | null }
  | { kind: 'neverStarted' }
  | { kind: 'unknown' };

/**
 * Whether the assistant pane should show its paused state, and what to say.
 *
 * Pauses on the first explicit `false` from `useMembershipAccess`, before the
 * subscription read settles, so the composer never accepts a turn the server
 * will refuse. The subscription is read from the network rather than the cache:
 * Billing settings may have cached it while it was still active.
 *
 * @returns `null` while the household may use the assistant (or access has not
 *   loaded); otherwise the pause, `unknown` until the subscription read
 *   resolves to a status the copy can name.
 */
export function useAssistantMembershipPause(): AssistantMembershipPause | null {
  const isPaused = useMembershipAccess() === false;
  const canManageBilling = useViewerCanManageBilling() === true;

  const { data, error } = useQuery<AssistantLapsedMembershipQuery, AssistantLapsedMembershipQueryVariables>(
    ASSISTANT_LAPSED_MEMBERSHIP,
    { skip: !isPaused || !canManageBilling, fetchPolicy: 'network-only' }
  );

  if (!isPaused) {
    return null;
  }
  if (data === undefined || error !== undefined) {
    return { kind: 'unknown' };
  }

  const subscription = data.currentSubscription ?? null;
  switch (subscription?.status) {
    case SubscriptionStatus.Canceled:
      return {
        kind: 'lapsed',
        endedAt: subscription.currentPeriodEnd != null ? new Date(subscription.currentPeriodEnd) : null,
      };
    case SubscriptionStatus.Paused:
      return { kind: 'lapsed', endedAt: null };
    case undefined:
    case SubscriptionStatus.Incomplete:
    case SubscriptionStatus.Failed:
    case SubscriptionStatus.Creating:
      // Nothing finished setting up, so nothing ended.
      return { kind: 'neverStarted' };
    default:
      // An entitling status the access read disagrees with — a webhook not yet mirrored.
      return { kind: 'unknown' };
  }
}
